import { kebabCase } from 'lodash';
import { createLogger } from '@owl-lib/logger';
import { deserialize, fetchWrapper } from '../fetch';
import type { ClaimFile, GetPresignedUploadResponse } from './interface';
const logger = createLogger(__filename);

export const FILE_CONTENT_TIMEOUT_LIMIT = 60_000;

const normalizeFile = (file: ClaimFile): ClaimFile => {
  if (!file.ingest_status && (file as ClaimFile & { status?: string }).status) {
    file.ingest_status = (file as ClaimFile & { status?: string }).status!;
  }
  return file;
};

export const fetchFiles = async (data: {
  dossier_id: string;
  stateDb?: boolean;
  limit?: number;
  offset?: number;
  tags?: string[];
  scopes?: string[];
}): Promise<{
  files: ClaimFile[];
  count: number;
  unsupported_files?: number;
  unsupported_file_types?: string[];
}> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/files`,
    API_BASE_URL
  );
  url.searchParams.set('include_count', 'true');
  if (!data.offset) {
    url.searchParams.set('include_unsupported_files', 'true');
  }
  if (data.limit != null) {
    url.searchParams.set('limit', `${data.limit}`);
  }
  if (data.offset != null) {
    url.searchParams.set('offset', `${data.offset}`);
  }
  if (data.tags) {
    data.tags.forEach((tag) => {
      url.searchParams.append('tags', tag);
    });
  }
  if (data.scopes) {
    data.scopes.forEach((scope) => {
      url.searchParams.append('scopes', scope);
    });
  }
  const result = await deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
  if (data.stateDb) {
    result.files = result.files?.map(normalizeFile);
  }
  return result;
};

export const fetchFile = async (data: {
  dossier_id: string;
  file_id: string;
  stateDb?: boolean;
}): Promise<ClaimFile> => {
  const url = new URL(`/app/v1/files/${data.file_id}`, API_BASE_URL);
  const file = await deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
  return data.stateDb ? normalizeFile(file) : file;
};

export const fetchFilePage = async (data: {
  dossierId: string;
  fileId: string;
  startPage: number;
  endPage: number;
}): Promise<{
  pages: { url: string; page: number }[];
}> => {
  const { dossierId, fileId, startPage, endPage } = data;
  const url = new URL(`/app/v1/files/${fileId}/pages`, API_BASE_URL);
  url.searchParams.set('dossier_id', dossierId);
  url.searchParams.set('start_page', `${startPage}`);
  url.searchParams.set('end_page', `${endPage}`);

  const start = Date.now();
  const response = await fetchWrapper(
    url.href,
    { method: 'GET', credentials: 'include' },
    FILE_CONTENT_TIMEOUT_LIMIT
  );
  const duration = Date.now() - start;
  if (duration > 1500) {
    logger.error(
      `File loading duration exceeded: file: ${fileId} for dossier: ${dossierId} took ${duration}ms`
    );
  }

  return deserialize(response);
};

export const fetchFilesOCR = async ({
  dossierId,
  fileId,
  dataVersion,
}: {
  dossierId: string;
  fileId: string;
  dataVersion: string;
}): Promise<{ url: string }> => {
  const url = new URL(
    `/app/v1/files/${fileId}/run/${dataVersion}/ocr`,
    API_BASE_URL
  );
  url.searchParams.set('dossier_id', dossierId);
  const presignedUrl = await fetchWrapper(
    url.href,
    { method: 'GET', credentials: 'include' },
    FILE_CONTENT_TIMEOUT_LIMIT
  );
  const deserializedUrl = await deserialize(presignedUrl);
  const ocrData = await globalThis.fetch(deserializedUrl.url);
  const ocrStrings = await ocrData.json();
  return ocrStrings;
};

export const fetchFileSource = async (data: {
  dossierId: string;
  fileId: string;
}): Promise<{ url: string }> => {
  const { dossierId, fileId } = data;
  const url = new URL(`/app/v1/files/${fileId}/contents`, API_BASE_URL);
  url.searchParams.set('dossier_id', dossierId);
  return deserialize(
    await fetchWrapper(
      url.href,
      { method: 'GET', credentials: 'include' },
      FILE_CONTENT_TIMEOUT_LIMIT
    )
  );
};

export const downloadFile = async (
  fileId: string
): Promise<{ url: string }> => {
  const url = new URL(`/app/v1/files/${fileId}/contents`, API_BASE_URL);

  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const updateFile = async (
  fileId: string,
  payload: { tags?: string[]; fileScope?: string | null; title?: string | null }
): Promise<void> => {
  const url = new URL(`/app/v1/files/${fileId}`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        tags: payload.tags,
        file_scope: payload.fileScope,
        title: payload.title,
      }),
    })
  );
};

export const deleteFile = async (fileId: string): Promise<void> => {
  const url = new URL(`/app/v1/files/${fileId}`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'DELETE',
      credentials: 'include',
    })
  );
};

export const uploadPresignedUrl = async (data: {
  fileName: string;
  contentType: string;
  uploadId: string;
}): Promise<{
  url: string;
  contentType: string;
  metadata: {
    fileName: string;
    uploadId: string;
  };
}> => {
  const { fileName, contentType, uploadId } = data;
  const url = new URL(`/app/v1/files/presigned`, API_BASE_URL);

  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        file_name: fileName,
        content_type: contentType,
        upload_id: uploadId,
      }),
    })
  );
};

export const createUpload = async (
  uploadId: string
): Promise<{ upload_id: string }> => {
  const url = new URL('/app/v1/files/upload', API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ upload_id: uploadId }),
    })
  );
};

export const createFile = async (data: {
  dossierId: string;
  fileName: string;
  uploadId: string;
  fileType: string;
  fileSize: number;
  s3Bucket: string;
  s3Key: string;
  title?: string;
}): Promise<string> => {
  const url = new URL('/app/v1/files', API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        dossier_id: data.dossierId,
        file_name: data.fileName,
        upload_id: data.uploadId,
        file_type: data.fileType,
        file_size: data.fileSize,
        raw_content_path: {
          s3_bucket: data.s3Bucket,
          s3_key: data.s3Key,
        },
        ...(data.title ? { title: data.title } : {}),
      }),
    })
  );
};

export const upload = async (data: GetPresignedUploadResponse) => {
  const { url: presignedUrl, contentType, file, metadata } = data;
  const metadataHeaders = Object.fromEntries(
    Object.entries(metadata).map(([k, v]) => [`x-amz-meta-${kebabCase(k)}`, v])
  );
  const url = new URL(presignedUrl);
  const paramKeys = Array.from(url.searchParams.keys()).map((k) =>
    k.toLowerCase()
  );

  if (
    !url.hostname.endsWith('.amazonaws.com') ||
    paramKeys.indexOf('x-amz-signature') === -1
  ) {
    logger.error(data, 'Not a valid Presigned S3 URL');
    throw new Error('Not a valid Presigned S3 URL');
  }

  return deserialize(
    await fetchWrapper(
      url.href,
      {
        method: 'PUT',
        headers: { ...metadataHeaders, 'content-type': contentType },
        body: await file.arrayBuffer(),
      },
      -1
    )
  );
};

export const fetchFileFilters = async (data: {
  dossier_id: string;
}): Promise<{ tags: string[]; scopes: string[] }> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/files/filters`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};
